import BoundaryMap from './BoundaryMap';
import CopyIcon from '@mui/icons-material/ContentCopyOutlined';
import Header from '../common/layout/Header';
import Notifier from '../common/Notifier';
import PolicyAuditLog from './PolicyAuditLog';
import PropTypes from 'prop-types';
import React from 'react';
import copy from 'copy-to-clipboard';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import {DateTime} from 'luxon';
import {Link as RouterLink, useParams} from 'react-router-dom';
import {Routes} from '@account/utils/route';
import {useGetCollection} from '@account/api/features.js';
import {useGetPolicy, useTogglePolicy} from '@account/api/archiveControl';

const CopyButton = ({target, text, ...buttonProps}) => {
  return (
    <Tooltip title={`Copy ${target}`}>
      <span>
        <IconButton
          aria-label={`copy ${target}`}
          size="small"
          onClick={() => {
            copy(text);
          }}
          {...buttonProps}
        >
          <CopyIcon sx={{fontSize: 16}} />
        </IconButton>
      </span>
    </Tooltip>
  );
};

CopyButton.propTypes = {
  target: PropTypes.string,
  text: PropTypes.string,
};

export const formatDateRange = ({
  range = {},
  separator = '–', // typographically correct en dash
  format = 'yyyy-MM-dd',
  fromFallback = '--',
  toFallback = '--',
}) => {
  const {start, end} = range;

  return [
    start
      ? DateTime.fromISO(start, {zone: 'utc'}).toFormat(format)
      : fromFallback,
    end ? DateTime.fromISO(end, {zone: 'utc'}).toFormat(format) : toFallback,
  ].join(` ${separator} `);
};

const Policy = () => {
  const {policyId} = useParams();

  const policyQuery = useGetPolicy(
    {policyId},
    {
      enabled: !!policyId,
    }
  );
  const togglePolicy = useTogglePolicy();

  const policy = policyQuery?.data;
  const collectionRef = policy?.body.collection_ref;
  const policyCollectionId = collectionRef?.split('/').pop();

  const collectionQuery = useGetCollection(
    {collectionId: policyCollectionId},
    {
      enabled: !!policy && !!policyCollectionId,
    }
  );

  if (policyQuery.isLoading) {
    return <Notifier open progress message="Loading policy..." />;
  }

  if (policyQuery.isError) {
    return <Notifier open message="There was an error loading the policy." />;
  }

  return (
    <Box sx={{paddingBlockEnd: 2}}>
      <Header
        name={policy.name}
        breadcrumbs={[
          {url: Routes.ARCHIVE_CONTROL, label: 'Archive Control Policies'},
          {label: policy.name},
        ]}
      >
        <Stack direction="row" sx={{gap: 1}}>
          <FormControlLabel
            size="small"
            control={
              <Switch
                checked={policy.enabled}
                size="small"
                onChange={event => {
                  const {checked} = event.target;
                  togglePolicy.mutate({policyId, enable: checked});
                }}
              />
            }
            label={`Turn policy ${policy.enabled ? 'off' : 'on'}`}
            sx={{
              '.MuiFormControlLabel-label': {
                fontSize: 14,
              },
            }}
          />
          <Button
            variant="contained"
            color="primary"
            component={RouterLink}
            to={`${Routes.ARCHIVE_CONTROL}/${policyId}/edit`}
          >
            Edit
          </Button>
        </Stack>
      </Header>

      <Stack direction="row" sx={{gap: 1, paddingBlockEnd: 2}}>
        <Typography variant="body2" color="text.secondary">
          <b>Created:</b>{' '}
          {DateTime.fromISO(policy.created_at, {zone: 'utc'}).toFormat('fff')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          <b>Updated:</b>{' '}
          {DateTime.fromISO(policy.updated_at, {zone: 'utc'}).toFormat('fff')}
        </Typography>
      </Stack>

      <Stack sx={{gap: 2}}>
        <Card variant="outlined">
          <CardHeader
            title="What this policy blocks"
            subheader=" Areas, time ranges, item types, and delay settings that are blocked
          for all organizations and users (unless exempt)."
          />
          <CardContent sx={{gap: 1, display: 'flex', flexDirection: 'column'}}>
            <Stack sx={{gap: 1}}>
              <Typography variant="subtitle2">
                Restricted area: {collectionQuery?.data?.['pl:ref']}
              </Typography>
              <Box sx={{height: 400}}>
                <BoundaryMap collection={collectionQuery?.data} />
              </Box>
            </Stack>
            <Stack direction="row" sx={{alignItems: 'center', gap: 0.5}}>
              <Typography variant="subtitle2">Item types:</Typography>
              <Typography variant="body2">
                {policy?.body.item_types?.join(', ')}
              </Typography>
            </Stack>
            {policy.body.delay && (
              <Stack direction="row" sx={{alignItems: 'center', gap: 1}}>
                <Typography variant="subtitle2">Delay:</Typography>
                <Typography variant="body2">
                  {`${policy.body.delay} days(s)`}
                </Typography>
              </Stack>
            )}
            {policy.body.start_date && (
              <Stack direction="row" sx={{alignItems: 'center', gap: 1}}>
                <Typography variant="subtitle2">Start Date:</Typography>
                <Typography variant="body2">
                  {DateTime.fromISO(policy.body.start_date, {
                    zone: 'utc',
                  }).toFormat('fff')}
                </Typography>
              </Stack>
            )}
            {policy.body.start_date && (
              <Typography variant="body2" color="text.secondary">
                Based on start date and delay the most recent acquisition date
                available is{' '}
                <b>
                  {DateTime.max(
                    DateTime.now().minus({days: policy.body.delay ?? 0}),
                    DateTime.fromISO(policy.body.start_date, {
                      zone: 'utc',
                    })
                  )
                    .minus({days: 1})
                    .toFormat('DDD')}
                </b>
              </Typography>
            )}

            {!policy.body.start_date && policy.body.delay && (
              <Typography variant="body2" color="text.secondary">
                Based on today’s date and the current delay, the most recent
                acquisition date available is{' '}
                <b>
                  {DateTime.now()
                    .minus({days: policy.body.delay})
                    .minus({days: 1})
                    .toFormat('DDD')}
                </b>
              </Typography>
            )}
            {!!policy.body.tois?.length && (
              <Stack sx={{gap: 1}}>
                <Stack>
                  <Typography variant="subtitle2">TOIs</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Data within these dates will be excluded
                  </Typography>
                </Stack>
                <Stack
                  direction="row"
                  sx={{
                    gap: 1,
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    maxHeight: 150,
                    overflow: 'auto',
                  }}
                >
                  {policy.body.tois.map((range, index) => {
                    return (
                      <Chip
                        size="small"
                        // eslint-disable-next-line react/no-array-index-key
                        key={index}
                        label={formatDateRange({range})}
                        variant="contained"
                      />
                    );
                  })}
                </Stack>
              </Stack>
            )}
          </CardContent>
        </Card>

        <Card variant="outlined">
          <CardHeader
            title="Who is exempt"
            subheader=" These organizations and users are exempt from this policy and will not
          be blocked."
          />
          <CardContent sx={{gap: 2, display: 'flex', flexDirection: 'column'}}>
            <Stack sx={{gap: 1}}>
              <Stack direction="row" sx={{gap: 2, alignItems: 'center'}}>
                <Typography variant="subtitle2">Organizations</Typography>
                <CopyButton
                  target="organization ids"
                  text={policy.excluded_organization_ids.toString()}
                  disabled={!policy.excluded_organization_ids.length}
                />
              </Stack>
              {policy.excluded_organization_ids.length ? (
                <Stack
                  direction="row"
                  sx={{
                    gap: 1,
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    maxHeight: 150,
                    overflow: 'auto',
                  }}
                >
                  {policy.excluded_organization_ids.map(orgId => (
                    <Chip
                      size="small"
                      key={orgId}
                      label={orgId}
                      component={RouterLink}
                      to={`${Routes.ORGS}/${orgId}`}
                      target="_blank"
                      variant="contained"
                      onClick={() => {
                        /** this is here to trigger on hover styles */
                      }}
                    />
                  ))}
                </Stack>
              ) : (
                <Typography variant="caption" color="text.secondary">
                  <i>No organizations added</i>
                </Typography>
              )}
            </Stack>
            <Stack sx={{gap: 1}}>
              <Stack direction="row" sx={{gap: 2, alignItems: 'center'}}>
                <Typography variant="subtitle2">Users</Typography>
                <CopyButton
                  target="user ids"
                  text={policy.excluded_user_ids.toString()}
                  disabled={!policy.excluded_user_ids.length}
                />
              </Stack>

              {policy.excluded_user_ids.length ? (
                <Stack
                  direction="row"
                  sx={{
                    gap: 1,
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    maxHeight: 150,
                    overflow: 'auto',
                  }}
                >
                  {policy.excluded_user_ids.map(userId => (
                    <Chip
                      size="small"
                      key={userId}
                      label={userId}
                      component={RouterLink}
                      to={`${Routes.USERS}/${userId}`}
                      target="_blank"
                      variant="contained"
                      onClick={() => {
                        /** this is here to trigger on hover styles */
                      }}
                    />
                  ))}
                </Stack>
              ) : (
                <Typography variant="caption" color="text.secondary">
                  <i>No users added</i>
                </Typography>
              )}
            </Stack>
          </CardContent>
        </Card>
        <Card variant="outlined">
          <CardHeader title="Audit Log" />
          <CardContent sx={{height: 500, display: 'flex'}}>
            <PolicyAuditLog policyId={policyId} />
          </CardContent>
        </Card>
      </Stack>

      <Notifier
        open={!!togglePolicy.isPending}
        progress
        message="Updating policy..."
      />
    </Box>
  );
};

export default Policy;
